import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useAdmin } from "../contexts/AdminContext";
import { CopyPrincipal } from "../components/CopyPrincipal";
import { ErrorBanner } from "../components/ErrorBanner";
import { StatusBadge } from "../components/StatusBadge";
import { formatDate, daysFromHealth, timeAgo } from "../lib/utils";
import type { ShopRecordWithStatus } from "../lib/types";

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

export function ShopsPage() {
  const { actor } = useAdmin();
  const [items, setItems] = useState<ShopRecordWithStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  useEffect(() => {
    if (!actor) return;
    (async () => {
      try {
        const res = await (actor as any).listShopsWithStatus() as { ok?: ShopRecordWithStatus[]; err?: unknown };
        if ("ok" in res) setItems(res.ok!);
        else setError("Failed to load shops");
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load shops");
      } finally {
        setLoading(false);
      }
    })();
  }, [actor]);

  const filtered = useMemo(() => {
    return items.filter(({ shop }) => {
      const matchesSearch =
        shop.name.toLowerCase().includes(search.toLowerCase()) ||
        shop.owner.toText().includes(search) ||
        shop.id.toText().includes(search);
      return matchesSearch;
    });
  }, [items, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(pageIndex, totalPages - 1);
  const page = filtered.slice(safePage * pageSize, (safePage + 1) * pageSize);
  const firstItem = filtered.length === 0 ? 0 : safePage * pageSize + 1;
  const lastItem = Math.min((safePage + 1) * pageSize, filtered.length);

  function handleSearch(val: string) {
    setSearch(val);
    setPageIndex(0);
  }

  return (
    <div>
      <h1 style={{ fontSize: 24, marginBottom: 24 }}>Shops</h1>

      {error && <ErrorBanner message={error} onDismiss={() => setError("")} />}

      <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
        <input
          placeholder="Search by name, owner or canister ID…"
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
          style={{ maxWidth: 280 }}
        />
      </div>

      {loading ? (
        <p style={{ color: "var(--color-muted)" }}>Loading…</p>
      ) : filtered.length === 0 ? (
        <p style={{ color: "var(--color-muted)" }}>No shops found.</p>
      ) : (
        <>
          <div className="card" style={{ padding: 0, overflowX: "auto" }}>
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Health</th>
                  <th>Owner</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {page.map(({ shop, status: statusOpt }) => {
                  const status = statusOpt[0];
                  return (
                    <tr key={shop.id.toText()}>
                      <td className="mono" style={{ color: "var(--color-muted)" }}>
                        {Number(shop.idx) + 1}
                      </td>
                      <td>
                        <Link
                          to={`/shops/${Number(shop.idx) + 1}`}
                          style={{ color: "var(--color-text)", fontWeight: 500 }}
                        >
                          {shop.name}
                        </Link>
                        <div style={{ marginTop: 2 }}>
                          <CopyPrincipal principal={shop.id.toText()} />
                        </div>
                      </td>
                      <td>
                        {status ? (() => {
                          const { label, stale } = timeAgo(status.recordedAt);
                          return (
                            <div>
                              <StatusBadge days={daysFromHealth(status.health)} health={status.health} />
                              <div style={{ fontSize: 11, marginTop: 3, color: stale ? "#f5c842" : "var(--color-muted)" }}>
                                {label}
                              </div>
                            </div>
                          );
                        })() : <span style={{ color: "var(--color-muted)", fontSize: 13 }}>—</span>}
                      </td>
                      <td><CopyPrincipal principal={shop.owner.toText()} /></td>
                      <td style={{ color: "var(--color-muted)", fontSize: 13, whiteSpace: "nowrap" }}>
                        {formatDate(shop.created)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 16, flexWrap: "wrap" }}>
            <span style={{ color: "var(--color-muted)", fontSize: 13 }}>
              Showing {firstItem}–{lastItem} of {filtered.length}
            </span>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: "auto" }}>
              <span style={{ color: "var(--color-muted)", fontSize: 13 }}>Per page:</span>
              <select
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setPageIndex(0); }}
              >
                {PAGE_SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <button onClick={() => setPageIndex((p) => p - 1)} disabled={safePage === 0}>Prev</button>
              <button onClick={() => setPageIndex((p) => p + 1)} disabled={safePage >= totalPages - 1}>Next</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
